/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { dateFormatter } from './dateFormatter';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export type GridlineInterval = { milliseconds: number } | { months: number };

// UTC calendar addition, clamping month-end dates rather than overflowing.
const addMonths = (timestamp: number, months: number): number => {
  const date = new Date(timestamp);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + months);
  const lastDay = new Date(date.getTime());
  lastDay.setUTCMonth(lastDay.getUTCMonth() + 1, 0);
  date.setUTCDate(Math.min(day, lastDay.getUTCDate()));
  return date.getTime();
};

export const getGridlineInterval = (
  start: number,
  end: number,
): GridlineInterval => {
  const duration = end - start;
  const bands = [
    [20 * MINUTE, MINUTE],
    [2 * HOUR, 10 * MINUTE],
    [4 * HOUR, 30 * MINUTE],
    [12 * HOUR, HOUR],
    [DAY, 3 * HOUR],
    [4 * DAY, 6 * HOUR],
    [7 * DAY, 12 * HOUR],
    [21 * DAY, DAY],
  ];
  const band = bands.find(([limit]) => duration <= limit);
  if (band) {
    return { milliseconds: band[1] };
  }
  if (end <= addMonths(start, 3)) {
    return { milliseconds: 7 * DAY };
  }
  return { months: end <= addMonths(start, 24) ? 1 : 6 };
};

export const getGridlineTimes = (start: number, end: number): number[] => {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
    return [];
  }
  const interval = getGridlineInterval(start, end);
  const times: number[] = [];
  if ('milliseconds' in interval) {
    // Unix epoch was Thursday; anchor seven-day lines to Monday midnight UTC.
    const anchor = interval.milliseconds === 7 * DAY ? 4 * DAY : 0;
    const first =
      Math.ceil((start - anchor) / interval.milliseconds) *
        interval.milliseconds +
      anchor;
    for (let time = first; time <= end; time += interval.milliseconds) {
      times.push(time);
    }
  } else {
    const date = new Date(start);
    date.setUTCDate(1);
    date.setUTCHours(0, 0, 0, 0);
    date.setUTCMonth(
      Math.floor(date.getUTCMonth() / interval.months) * interval.months,
    );
    while (date.getTime() < start) {
      date.setUTCMonth(date.getUTCMonth() + interval.months);
    }
    while (date.getTime() <= end) {
      times.push(date.getTime());
      date.setUTCMonth(date.getUTCMonth() + interval.months);
    }
  }
  return times;
};

export const getGridlineLabel = (
  timestamp: number,
  interval: GridlineInterval,
): [string, string] => {
  const formats =
    'months' in interval
      ? ['MMM', 'yyyy']
      : [interval.milliseconds < DAY * 7 ? 'eee' : 'dd MMM', 'HH:mm'];
  return [
    dateFormatter(timestamp, formats[0]),
    dateFormatter(timestamp, formats[1]),
  ];
};
